import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Modal,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Form, Formik, FormikHelpers } from "formik";
import axios from "../../api";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import * as Yup from "yup";
import { useCallback, useEffect, useState, useMemo } from "react";
import { AxiosError } from "axios";

const customScrollbarStyle = {
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#44444461",
    borderRadius: "8px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#666",
  },
};

interface Asset {
  asset_type_id: number;
  asset_type_name: string;
}

interface SubscriptionType {
  name: string;
  description?: string;
}

interface FormValues {
  free_service_custom_dates: any;
  free_service_count: unknown;
  include_free_service: any;
  asset_type_id: number | "";
  subscription_type_name: string;
  description: string;
  price: number | "";
  duration_value: number | "";
  duration_unit: "days" | "months" | "";
  custom_name: string;
}

interface EditData {
  subscription_type_id: number;
  asset_type_id: number;
  subscription_type_name: string;
  description: string;
  price: number;
  duration_in_days: number;
  include_free_service?: number;
  free_service_count?: number | null;
}

interface AddSubscriptionTypeProps {
  isOpen: boolean;
  onClose: () => void;
  fetchData: () => void | Promise<void>;
  editData?: EditData | null;
  /** When true, render form only (no Modal) for use inside SlideOverPanel */
  embedded?: boolean;
}

const validationSchema = Yup.object({
  asset_type_id: Yup.number().required("Asset is required"),
  subscription_type_name: Yup.string().required(
    "Subscription name is required"
  ),
  description: Yup.string().required("Description is required"),
  price: Yup.number()
    .typeError("Price must be a number")
    .required("Price is required")
    .min(0, "Price must be positive"),
  duration_value: Yup.number()
    .typeError("Duration must be a number")
    .required("Duration is required")
    .min(0, "Duration must be greater than or equal to 0"),
  duration_unit: Yup.string().required("Please select a unit"),
  include_free_service: Yup.boolean(),
  free_service_count: Yup.number().when(
    "include_free_service",
    ([include_free_service], schema) => {
      return include_free_service
        ? schema.required("Count required").min(1)
        : schema.notRequired();
    }
  ),
});

const AddSubscriptionType: React.FC<AddSubscriptionTypeProps> = ({
  isOpen,
  onClose,
  fetchData,
  editData,
  embedded,
}) => {
  const { fetchError, fetchSuccess } = useSnackbar();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

  const fetchAssets = useCallback(async () => {
    try {
      const response = await axios.get("/asset-types", {
        params: { is_active: 1 },
      });
      const assetData = Array.isArray(response.data.list)
        ? response.data.list
        : [];
      setAssets(assetData);
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        console.error(error.response.data?.error);
      }
    }
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  useEffect(() => {
    if (editData && assets.length > 0) {
      const foundAsset = assets.find(
        (a) => a.asset_type_id === editData.asset_type_id
      );
      setSelectedAsset(foundAsset ?? null);
    }
  }, [editData, assets]);

  // Asset-wise predefined subscriptions
  const assetSpecificSubscriptions: Record<string, SubscriptionType[]> = {
    "Air Conditioner": [
      {
        name: "AMC (Annual Maintenance Contract)",
        description:
          "Covers regular preventive maintenance and minor repairs throughout the year.",
      },
      {
        name: "Comprehensive AMC",
        description:
          "Includes full maintenance and replacement of major components (parts + labor).",
      },
      {
        name: "Non-Comprehensive AMC",
        description:
          "Includes service and labor charges; spare parts billed separately.",
      },
      {
        name: "Quarterly Maintenance",
        description: "Routine maintenance every three months.",
      },
      {
        name: "Preventive Maintenance Contract (PMC)",
        description:
          "Focuses on preventive checks for cooling and electrical components.",
      },
    ],
    Refrigerator: [
      {
        name: "Annual Maintenance Contract",
        description:
          "Year-round servicing and preventive maintenance for refrigerators.",
      },
      {
        name: "Comprehensive AMC",
        description: "Covers parts + labor, including compressor repairs.",
      },
      {
        name: "Half-Yearly Maintenance",
        description: "Two preventive visits per year.",
      },
    ],
    "Washing Machine": [
      {
        name: "AMC (Annual Maintenance Contract)",
        description: "Annual preventive maintenance for washing machine.",
      },
      {
        name: "Quarterly Maintenance",
        description: "Quarterly check-ups and drum cleaning service.",
      },
      {
        name: "On-Demand Service",
        description: "Pay-per-visit service for breakdowns or installation.",
      },
    ],
    Lift: [
      {
        name: "Monthly Maintenance Contract",
        description: "Monthly inspection and lubrication of lift components.",
      },
      {
        name: "Comprehensive AMC",
        description: "Full maintenance and replacement of critical parts.",
      },
      {
        name: "Non-Comprehensive AMC",
        description: "Covers service; spare parts charged extra.",
      },
    ],
    Television: [
      {
        name: "Extended Warranty",
        description:
          "Additional warranty period after manufacturer warranty expires.",
      },
      {
        name: "On-Demand Service",
        description: "Pay-per-visit repair for display or circuit issues.",
      },
    ],
    Laptop: [
      {
        name: "Extended Warranty",
        description: "Covers repair/replacement after standard warranty ends.",
      },
      {
        name: "On-Demand Service",
        description: "Support for OS, hardware, and part replacement.",
      },
    ],
    WiFi: [
      {
        name: "Annual Maintenance Contract",
        description: "Annual router and network maintenance.",
      },
      {
        name: "Installation & Commissioning",
        description: "Router setup and signal optimization service.",
      },
    ],
    Fan: [
      {
        name: "Quarterly Maintenance",
        description: "Cleaning and lubrication every three months.",
      },
      {
        name: "On-Demand Service",
        description: "Repair and installation support.",
      },
    ],
    "CC Camera": [
      {
        name: "AMC (Annual Maintenance Contract)",
        description: "Yearly maintenance for CCTV systems and recording setup.",
      },
      {
        name: "Comprehensive AMC",
        description: "Includes wiring, camera cleaning, and DVR maintenance.",
      },
      {
        name: "On-Demand Service",
        description: "Repair or lens replacement on request.",
      },
    ],
  };

  // Dynamically determine subscriptions based on selected asset
  const filteredSubscriptions = useMemo(() => {
    const assetName = selectedAsset?.asset_type_name;
    const subs = assetSpecificSubscriptions[assetName ?? ""] ?? [];
    return [...subs, { name: "Other", description: "" }];
  }, [selectedAsset]);

  // Helper function to calculate initial duration value
  const getInitialDurationValue = (
    editData: EditData | null | undefined
  ): number | "" => {
    if (!editData) return "";

    const isMonths = editData.duration_in_days % 30 === 0;
    return isMonths
      ? editData.duration_in_days / 30
      : editData.duration_in_days;
  };

  // Helper function to calculate initial duration unit
  const getInitialDurationUnit = (
    editData: EditData | null | undefined
  ): "days" | "months" | "" => {
    if (!editData) return "";

    const isMonths = editData.duration_in_days % 30 === 0;
    return isMonths ? "months" : "days";
  };

  const formContent = (
    <Box
      sx={{
        ...(embedded
          ? { p: 2, overflow: "auto", ...customScrollbarStyle }
          : {
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              borderRadius: "8px",
              boxShadow: 24,
              p: 4,
              minWidth: 500,
              maxWidth: 650,
              maxHeight: "calc(100vh - 100px)",
              overflow: "auto",
              ...customScrollbarStyle,
            }),
      }}
      >
      {!embedded && (
        <Typography variant="h6" component="h2" gutterBottom>
          {editData ? "Edit Subscription Type" : "Add Subscription Type"}
        </Typography>
      )}

        <Formik
          initialValues={{
            asset_type_id: editData?.asset_type_id ?? "",
            subscription_type_name: editData?.subscription_type_name ?? "",
            description: editData?.description ?? "",
            price: editData?.price ?? "",
            duration_value: getInitialDurationValue(editData),
            duration_unit: getInitialDurationUnit(editData),
            custom_name: "",
            include_free_service: editData ? !!editData.include_free_service : false,
            free_service_count: editData?.free_service_count != null ? editData.free_service_count : "",
            free_service_custom_dates: [],
          }}
          validationSchema={validationSchema}
          onSubmit={async (
            values: FormValues,
            { resetForm }: FormikHelpers<FormValues>
          ) => {
            try {
              let duration_in_days = values.duration_value;
              if (values.duration_unit === "months") {
                duration_in_days = Number(values.duration_value) * 30;
              }

              const payload = {
                asset_type_id: values.asset_type_id,
                subscription_type_name:
                  values.subscription_type_name === "Other"
                    ? values.custom_name
                    : values.subscription_type_name,
                description: values.description,
                price: values.price,
                duration_in_days,
                include_free_service: values.include_free_service,
                free_service_count: values.include_free_service
                  ? values.free_service_count
                  : 0,
                free_service_split_mode: values.include_free_service
                  ? "auto"
                  : null,
                free_service_custom_dates: [],
                free_service_window_days: values.include_free_service ? 7 : null,
              };

              let response;
              if (editData) {
                response = await axios.put(
                  `/subscription-types/${editData.subscription_type_id}`,
                  payload
                );
              } else {
                response = await axios.post("/subscription-types", payload);
              }

              fetchSuccess(response.data.message);
              fetchData();
              resetForm();
              onClose();
            } catch (error) {
              if (error instanceof AxiosError && error.response) {
                fetchError(
                  error.response?.data?.error ??
                    "Error saving subscription type"
                );
              }
            }
          }}
        >
          {({ values, errors, touched, handleChange, setFieldValue }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={assets}
                      getOptionLabel={(option) => option.asset_type_name ?? ""}
                      value={selectedAsset}
                      onChange={(event, value) => {
                        setSelectedAsset(value);
                        setFieldValue(
                          "asset_type_id",
                          value?.asset_type_id ?? ""
                        );
                        setFieldValue("subscription_type_name", "");
                        setFieldValue("description", "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Asset Type"
                          error={Boolean(
                            errors.asset_type_id && touched.asset_type_id
                          )}
                          helperText={
                            touched.asset_type_id && errors.asset_type_id
                          }
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={filteredSubscriptions}
                      getOptionLabel={(option) => option.name}
                      value={
                        filteredSubscriptions.find(
                          (opt) => opt.name === values.subscription_type_name
                        ) ?? null
                      }
                      onChange={(event, value) => {
                        if (value) {
                          setFieldValue("subscription_type_name", value.name);
                          setFieldValue("description", value.description ?? "");
                        } else {
                          setFieldValue("subscription_type_name", "");
                          setFieldValue("description", "");
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select or type subscription"
                          error={Boolean(
                            touched.subscription_type_name &&
                              errors.subscription_type_name
                          )}
                          helperText={
                            touched.subscription_type_name &&
                            errors.subscription_type_name
                          }
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                {values.subscription_type_name === "Other" && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <TextField
                        name="custom_name"
                        placeholder="Enter custom name"
                        value={values.custom_name}
                        onChange={handleChange}
                        error={Boolean(
                          touched.custom_name && errors.custom_name
                        )}
                        helperText={touched.custom_name && errors.custom_name}
                      />
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12} md={12}>
                  <FormControl fullWidth>
                    <TextField
                      name="description"
                      placeholder="Enter description"
                      value={values.description}
                      onChange={handleChange}
                      disabled={filteredSubscriptions.some(
                        (s) =>
                          s.name === values.subscription_type_name &&
                          s.name !== "Other"
                      )}
                      multiline
                      rows={3}
                      sx={{ minHeight: 100 }}
                      error={Boolean(touched.description && errors.description)}
                      helperText={touched.description && errors.description}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      name="price"
                      placeholder="Enter price"
                      type="number"
                      value={values.price}
                      onChange={handleChange}
                      inputProps={{ min: 0, step: "0.01" }}
                      error={Boolean(touched.price && errors.price)}
                      helperText={touched.price && errors.price}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      select
                      name="duration_unit"
                      value={values.duration_unit}
                      onChange={handleChange}
                      error={Boolean(
                        touched.duration_unit && errors.duration_unit
                      )}
                      helperText={touched.duration_unit && errors.duration_unit}
                      variant="outlined"
                      placeholder="Choose Days or Months"
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (selected) => {
                          if (!selected) {
                            return (
                              <Box component="span" sx={{ color: "#aaa" }}>
                                Choose Days or Months
                              </Box>
                            );
                          }
                          return selected === "days" ? "Days" : "Months";
                        },
                      }}
                    >
                      <MenuItem value="days">Days</MenuItem>
                      <MenuItem value="months">Months</MenuItem>
                    </TextField>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      name="duration_value"
                      placeholder="Duration"
                      type="number"
                      value={values.duration_value}
                      onChange={handleChange}
                      inputProps={{ min: 0, step: "1" }}
                      error={Boolean(
                        touched.duration_value && errors.duration_value
                      )}
                      helperText={
                        touched.duration_value && errors.duration_value
                      }
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.include_free_service}
                        onChange={(e) =>
                          setFieldValue(
                            "include_free_service",
                            e.target.checked
                          )
                        }
                        color="success"
                      />
                    }
                    label="Include Free Service"
                  />
                </Grid>
                {values.include_free_service && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <TextField
                        name="free_service_count"
                        placeholder="Free Service Count"
                        type="number"
                        value={values.free_service_count}
                        onChange={handleChange}
                        error={Boolean(
                          touched.free_service_count &&
                            errors.free_service_count
                        )}
                        helperText={
                          touched.free_service_count &&
                          errors.free_service_count
                        }
                      />
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      className="success-btn"
                      sx={{ minWidth: 100 }}
                      variant="contained"
                      type="submit"
                      color="success"
                    >
                      Save
                    </Button>
                    <Button
                      className="cancel-btn"
                      sx={{ minWidth: 100, ml: 2.5 }}
                      variant="contained"
                      color="warning"
                      onClick={onClose}
                      type="button"
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Box>
  );

  if (embedded) {
    return isOpen ? formContent : null;
  }
  return (
    <Modal open={isOpen} onClose={onClose}>
      {formContent}
    </Modal>
  );
};

export default AddSubscriptionType;
